import React, { useState, useRef, useCallback } from 'react';
import { Label, M } from './ServicioUI';
import { LuPencil } from 'react-icons/lu';

// sinPrecio: el técnico no ve precios. Un ítem con `bloqueado` (ya guardado) no se edita ni se quita.
export default function TicketItemsList({ ticketItems, editarItem, eliminarItem, onReorder, sinPrecio = false, titulo = null }) {
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);
    const longPressTimer = useRef(null);
    const touchStartY = useRef(null);
    const containerRef = useRef(null);

    // Long-press para activar drag (touch)
    const handleTouchStart = useCallback((idx, e) => {
        touchStartY.current = e.touches[0].clientY;
        longPressTimer.current = setTimeout(() => {
            setDragIdx(idx);
            // Vibración háptica si está disponible
            if (navigator.vibrate) navigator.vibrate(30);
        }, 400);
    }, []);

    const handleTouchMove = useCallback((e) => {
        // Si aún no activó drag, cancelar si se movió mucho
        if (dragIdx === null) {
            if (longPressTimer.current && touchStartY.current !== null) {
                const dy = Math.abs(e.touches[0].clientY - touchStartY.current);
                if (dy > 10) {
                    clearTimeout(longPressTimer.current);
                    longPressTimer.current = null;
                }
            }
            return;
        }
        e.preventDefault();
        // Determinar sobre qué item está el dedo
        const touch = e.touches[0];
        const container = containerRef.current;
        if (!container) return;
        const children = Array.from(container.children);
        for (let i = 0; i < children.length; i++) {
            const rect = children[i].getBoundingClientRect();
            if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
                setOverIdx(i);
                return;
            }
        }
    }, [dragIdx]);

    const handleTouchEnd = useCallback(() => {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx && onReorder) {
            onReorder(prev => {
                const copia = [...prev];
                const [moved] = copia.splice(dragIdx, 1);
                copia.splice(overIdx, 0, moved);
                return copia;
            });
        }
        setDragIdx(null);
        setOverIdx(null);
    }, [dragIdx, overIdx, onReorder]);

    if (ticketItems.length === 0) return null;

    return (
        <div>
            <Label>
                {titulo || `${ticketItems.length} equipo${ticketItems.length > 1 ? 's' : ''} en el ticket`}
                {onReorder && ticketItems.length > 1 && (
                    <span className="text-caption font-medium text-muted ml-2">
                        mantener presionado para reordenar
                    </span>
                )}
            </Label>
            <div
                ref={containerRef}
                className="flex flex-col gap-2"
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {ticketItems.map((it, idx) => {
                    const isDragging = dragIdx === idx;
                    const isOver = dragIdx !== null && overIdx === idx && dragIdx !== idx;
                    return (
                        <div
                            key={idx}
                            onTouchStart={(e) => onReorder && ticketItems.length > 1 ? handleTouchStart(idx, e) : null}
                            onTouchCancel={() => { clearTimeout(longPressTimer.current); setDragIdx(null); setOverIdx(null); }}
                            className={`flex items-center justify-between p-3 rounded-xl border transition-all
                                bg-chip
                                ${isDragging
                                    ? 'opacity-50 scale-95 border-brand-red'
                                    : isOver
                                        ? 'border-brand-amber shadow-md'
                                        : 'border-black/10 dark:border-white/10'
                                }
                                ${dragIdx !== null ? 'select-none' : ''}
                            `}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                {/* Handle visual de drag */}
                                {onReorder && ticketItems.length > 1 && (
                                    <span className={`text-label flex-shrink-0 transition-colors ${dragIdx !== null ? 'text-[#D48800]' : 'text-muted'}`}>
                                        ⠿
                                    </span>
                                )}
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-label font-black flex-shrink-0 bg-brand-red">
                                    {idx + 1}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-body font-bold truncate text-ink">
                                        {it.equipoSerial || 'Sin S/N'}
                                    </p>
                                    <p className="text-caption truncate text-muted">
                                        {it.resumenTexto}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                {!sinPrecio && <M valor={it.totalCalculado} className="text-body font-black text-ink" />}
                                {it.bloqueado ? <span className="text-caption font-bold text-muted">Ya cargado</span> : <>
                                <button onClick={() => editarItem(idx)}
                                    className="w-9 h-9 rounded-lg flex items-center justify-center text-label bg-[#D48800]/20 text-brand-amber active:scale-90 transition-all"
                                    title="Editar equipo"><LuPencil size={14} /></button>
                                <button onClick={() => eliminarItem(idx)}
                                    className="w-9 h-9 rounded-lg flex items-center justify-center text-label bg-[#D13A28]/10 text-brand-red active:scale-90 transition-all"
                                    title="Eliminar equipo">✕</button>
                                </>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
